import type { Node as PMNode } from '@tiptap/pm/model';

import type { TMap } from '@shared/types';
import { EBulkOperationStatus } from '@modules/items/types/bulkOperation.types';
import type { TRefData } from '@modules/documents/types/reference.types';
import type { TBlockData } from '@modules/documents/types/blocks.types';
import type { TResolvedItemNames } from '@modules/items/types/items.types';
import { EAiEditSuggestionKind, type TAiEditRange } from '@modules/documents/tiptap/plugins/AiEditReviewPlugin';

import type { TAiAssistantStatusMessage, TAiCitationResponse, TAiModeRequest, TAiContentSelection } from '../types';

export { EAiEditSuggestionKind };

// WARNING Mirrored in FE/BE - keep values in sync.
// Counterpart: backend/src/ai/useCases/edit/types/edit.types.ts
export enum EAiEditResultType {
  OUT_OF_SCOPE = 'OUT_OF_SCOPE',
  NO_ANSWER_FOUND = 'NO_ANSWER_FOUND',
  NOTHING_TO_CHANGE = 'NOTHING_TO_CHANGE',
  UNCLEAR_REQUEST = 'UNCLEAR_REQUEST',
  ANSWER = 'ANSWER',
}

export enum EAiEditState {
  IN_REVIEW = 'IN_REVIEW',
  SUBMITTED = 'SUBMITTED',
  CANCELLED = 'CANCELLED',
}

type TAiEditSuggestionBase = {
  id: string;
  elementId: string;
  original?: string;
};

/** A ProseMirror node as the backend serialized it, ready for `schema.nodeFromJSON`. */
export type TAiEditSuggestionNode = Record<string, unknown>;

export type TAiEditSuggestion =
  | (TAiEditSuggestionBase & {
      kind: EAiEditSuggestionKind.INSERT_BEFORE | EAiEditSuggestionKind.INSERT_AFTER | EAiEditSuggestionKind.REPLACE;
      node: TAiEditSuggestionNode;
    })
  | (TAiEditSuggestionBase & {
      kind: EAiEditSuggestionKind.REMOVE;
      node?: never;
    });

export type TAiEditInput = {
  documentId: string;
  prompt: string;
  contentSelection?: TAiContentSelection;
};

export type TAiEditRequest = TAiEditInput & TAiModeRequest;

export type TAiEditResultResponse = {
  answer: string;
  citationSources: TAiCitationResponse[];
  suggestions: TAiEditSuggestion[];
  refsData?: TMap<string, TRefData>;
  blocksData?: TMap<string, TBlockData>;
  /** Field id -> the options of a dropdown the suggestions insert or give new options; a value-only change has no entry. */
  templateFieldOptions: TMap<string, string[]>;
  resolvedItemNames?: TResolvedItemNames;
  followUpPrompt?: string | null;
};

export type TAiEditDecision = {
  suggestionId: string;
  accepted: boolean;
};

export type TAiEditRunResponse = {
  runId: string;
  status: EBulkOperationStatus;
  input: TAiEditInput;
  resultType?: EAiEditResultType;
  mockResponse?: boolean;
  result?: TAiEditResultResponse;
  errorMessage?: string;
  progressMessage?: string;
  createdDate: string;
  startedDate?: string;
  completedDate?: string;
  editState: EAiEditState;
  decisions: TAiEditDecision[];
  decidedDate?: string;
};

export type TPlannedAiEditTarget = TAiEditRange & {
  node: PMNode;
};

type TPlannedAiEditSuggestionBase = TAiEditSuggestionBase & {
  /** The saved node `elementId` names; absent when it names a node an earlier suggestion inserted, or nothing. */
  target?: TPlannedAiEditTarget;
};

export type TPlannedAiEditInsertion = TPlannedAiEditSuggestionBase & {
  kind: EAiEditSuggestionKind.INSERT_BEFORE | EAiEditSuggestionKind.INSERT_AFTER;
  node: PMNode;
};

export type TPlannedAiEditDestruction = TPlannedAiEditSuggestionBase &
  ({ kind: EAiEditSuggestionKind.REPLACE; node: PMNode } | { kind: EAiEditSuggestionKind.REMOVE });

export type TPlannedAiEditSuggestion = TPlannedAiEditInsertion | TPlannedAiEditDestruction;

/** One saved target with every suggestion that lands around it, flattened into rendered order. */
export type TAiEditTargetGroup = {
  elementId: string;
  target: TPlannedAiEditTarget;
  before: TPlannedAiEditInsertion[];
  destructive: TPlannedAiEditDestruction[];
  after: TPlannedAiEditInsertion[];
};

export type TPlannedAiEdit = {
  baselineDoc: PMNode;
  suggestions: TPlannedAiEditSuggestion[];
  /** Copied/unsettled atom id -> the placement id its preview display resolves through. */
  previewAtomSources: ReadonlyMap<string, string>;
};

type TAiEditReviewResultBase = {
  runId: string;
  answer: string;
  citationSources: TAiCitationResponse[];
  followUpPrompt?: string | null;
};

/** Only a result with validated suggestions enters the editor review. */
export type TAiEditReviewResult = TAiEditReviewResultBase &
  (
    | {
        status: 'review';
        plan: TPlannedAiEdit;
        navigationElementIds: string[];
        dropdownOptions: TMap<string, string[]>;
      }
    | { status: 'accepted'; saveFailed: boolean }
    | { status: 'mock-response' }
    | { status: 'no-change'; statusMessage: TAiAssistantStatusMessage }
  );
